import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { SidebarWithHeader, OrderDetails } from '../../components/admin';
import { useAdminOrderStore } from '../../stores';
import { admin_order_url } from '../../utils/constants';
import { FaFilePdf } from 'react-icons/fa';
import { toast } from 'react-toastify';

const NEXT_STATUS_OPTIONS = {
  processing: [
    { name: 'Confirmed', value: 'confirmed' },
    { name: 'Cancelled', value: 'cancelled' },
  ],
  confirmed: [
    { name: 'Shipped', value: 'shipped' },
    { name: 'Cancelled', value: 'cancelled' },
  ],
  shipped: [
    { name: 'Delivered', value: 'delivered' },
    { name: 'Cancelled', value: 'cancelled' },
  ],
  delivered: [],
  returned: [],
  cancelled: [],
};

function SingleOrderPage() {
  const { id } = useParams();
  const {
    single_order_loading: loading,
    single_order_error: error,
    single_order: order,
    single_order_status,
    fetchSingleOrder,
    updateOrderStatus,
    updateReturnStatus,
  } = useAdminOrderStore();

  const statusList = useMemo(
    () => NEXT_STATUS_OPTIONS[single_order_status] || [],
    [single_order_status]
  );

  const [trackingInfo, setTrackingInfo] = useState({
    trackingNumber: '',
    carrier: 'Australia Post'
  });

  const handleChange = async (e) => {
    const status = e.target.value;
    if (!status) return;
    const response = await updateOrderStatus(status, id, trackingInfo.trackingNumber, trackingInfo.carrier);
    if (response.success) {
      toast.success(`Order ${response.status}`, { position: 'top-center' });
      fetchSingleOrder(id);
    } else {
      toast.error(response.message, { position: 'top-center' });
    }
  };

  const handleReturnAction = async (status) => {
    const response = await updateReturnStatus(status, id);
    if (response.success) {
      toast.success(response.message, { position: 'top-center' });
      fetchSingleOrder(id);
    } else {
      toast.error(response.message, { position: 'top-center' });
    }
  };

  const handleDownloadPDF = async (type) => {
    try {
      const response = await axios.get(`${admin_order_url}${id}/${type}`, {
        responseType: 'blob',
        withCredentials: true
      });
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      const downloadLink = document.createElement('a');
      downloadLink.href = fileURL;
      downloadLink.download = `${type}_${id}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(fileURL);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to download ${type}. Please try again.`);
    }
  };

  useEffect(() => {
    fetchSingleOrder(id);
    // eslint-disable-next-line
  }, [id]);


  if (loading) {
    return (
      <SidebarWithHeader>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-bronze/20 border-t-gold rounded-full animate-spin" />
        </div>
      </SidebarWithHeader>
    );
  }

  if (error) {
    return (
      <SidebarWithHeader>
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-lg font-editorial text-red-500 mb-3">There was an error</p>
          <p className="text-sm text-bronze/50">Unable to load order details. Please try again.</p>
          <p className="text-xs text-bronze/30 mt-2">Order ID: {id}</p>
        </div>
      </SidebarWithHeader>
    );
  }

  return (
    <SidebarWithHeader>
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-bronze/40 mb-3">
          <span className="hover:text-gold cursor-pointer transition-colors" onClick={() => window.location.href = '/admin/orders'}>Orders</span>
          <span>/</span>
          <span className="text-bronze">Archive #{String(id || '').slice(-8)}</span>
        </div>
        <h1 className="text-3xl font-editorial font-black text-bronze uppercase tracking-widest">Order Details</h1>
      </div>

      {/* Page Header & Controls */}
      <div className="bg-white border border-bronze/10 rounded-xl p-6 mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">

          {/* Status Control Section */}
          <div className="flex flex-col gap-2">
            <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-bronze/50">Order Status Management</h2>
            <div className="flex flex-wrap items-center gap-4">
              {order && order.returnStatus !== 'none' ? (
                <span className="bg-red-50 border border-red-200 text-red-700 rounded px-4 py-2.5 text-[10px] font-black uppercase tracking-widest cursor-not-allowed">
                  Locked (Return Active)
                </span>
              ) : statusList.length === 0 ? (
                <span className="bg-gray-50 border border-gray-200 text-gray-500 rounded px-4 py-2.5 text-[10px] font-black uppercase tracking-widest cursor-not-allowed">
                  {single_order_status ? `${single_order_status} (Final)` : 'Loading…'}
                </span>
              ) : (
                <select
                  value=""
                  onChange={handleChange}
                  className="bg-champagne/30 border border-bronze/20 rounded-lg px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-bronze focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all cursor-pointer min-w-[200px]"
                >
                  <option value="" disabled>
                    Current: {single_order_status} — move to…
                  </option>
                  {statusList.map((status, index) => (
                    <option key={index} value={status.value}>{status.name}</option>
                  ))}
                </select>
              )}

              {single_order_status === 'confirmed' && (!order || order.returnStatus === 'none') && (
                <div className="flex gap-3 animate-in fade-in slide-in-from-left-4 duration-500">
                  <input
                    type="text"
                    placeholder="Tracking #"
                    value={trackingInfo.trackingNumber}
                    onChange={(e) => setTrackingInfo({ ...trackingInfo, trackingNumber: e.target.value })}
                    className="bg-white border border-bronze/20 rounded-lg px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-bronze focus:border-gold focus:ring-1 focus:ring-gold outline-none w-40 placeholder:text-bronze/30 transition-all"
                  />
                  <select
                    value={trackingInfo.carrier}
                    onChange={(e) => setTrackingInfo({ ...trackingInfo, carrier: e.target.value })}
                    className="bg-white border border-bronze/20 rounded-lg px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-bronze focus:border-gold focus:ring-1 focus:ring-gold outline-none cursor-pointer transition-all"
                  >
                    <option value="Australia Post">Australia Post</option>
                    <option value="DHL Express">DHL</option>
                    <option value="StarTrack">StarTrack</option>
                    <option value="Courier Please">Courier Please</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Document Actions */}
          <div className="flex flex-col gap-2 md:items-end">
            <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-bronze/50">Documentation</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleDownloadPDF('invoice')}
                className="flex items-center justify-center gap-2 bg-white text-bronze px-6 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-bronze hover:text-white transition-all border border-bronze/20 shadow-sm"
              >
                <FaFilePdf size={14} className="text-red-500 group-hover:text-white transition-colors" />
                <span>Invoice</span>
              </button>
              <button
                onClick={() => handleDownloadPDF('packingslip')}
                className="flex items-center justify-center gap-2 bg-bronze text-white px-6 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-gold transition-all shadow-md shadow-bronze/20 border border-transparent"
              >
                <FaFilePdf size={14} className="text-white/80" />
                <span>Packing Slip</span>
              </button>
            </div>
          </div>

        </div>
      </div>

      {order && order.returnStatus !== 'none' && order.returnStatus !== 'completed' && (
        <div className="bg-bronze text-champagne p-8 rounded-lg mb-5 space-y-6 animate-in fade-in slide-in-from-top-4 duration-500 shadow-2xl shadow-bronze/20">
          <div className="flex items-center justify-between border-b border-champagne/10 pb-6">
            <div className="space-y-1">
              <span className="text-[10px] font-black uppercase tracking-[0.4em] opacity-60">Archival Return Submission</span>
              <h4 className="text-xl font-editorial font-bold tracking-tight uppercase">Status: {order.returnStatus}</h4>
            </div>
            <div className="flex gap-3">
              {order.returnStatus === 'requested' && (
                <>
                  <button
                    onClick={() => handleReturnAction('approved')}
                    className="px-6 py-3 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20 active:scale-95"
                  >
                    Approve Request
                  </button>
                  <button
                    onClick={() => handleReturnAction('rejected')}
                    className="px-6 py-3 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 active:scale-95"
                  >
                    Reject Submission
                  </button>
                </>
              )}
              {order.returnStatus === 'approved' && (
                <button
                  onClick={() => handleReturnAction('processing')}
                  className="px-6 py-3 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 active:scale-95"
                >
                  Mark as processing
                </button>
              )}
              {order.returnStatus === 'processing' && (
                <button
                  onClick={() => handleReturnAction('completed')}
                  className="px-6 py-3 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20 active:scale-95"
                >
                  Finalize & Restore Stock
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-2">
            <div className="space-y-2">
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Return Reason</span>
              <p className="text-sm font-medium leading-relaxed italic border-l-2 border-gold/30 pl-4">
                "{order.returnReason || 'No reason provided by client'}"
              </p>
            </div>
            <div className="space-y-2">
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Submission Metadata</span>
              <div className="text-[10px] font-bold uppercase tracking-widest space-y-1">
                <p>Requested: <span className="text-gold">{new Date(order.returnRequestedAt).toLocaleDateString('en-AU')}</span></p>
                {order.returnedAt && <p>Restored: <span className="text-gold">{new Date(order.returnedAt).toLocaleDateString('en-AU')}</span></p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Details */}
      <div className="bg-white border border-bronze/10 rounded-lg p-6">
        {order && order._id ? (
          <OrderDetails {...order} order_status={single_order_status} />
        ) : (
          <div className="flex flex-col items-center justify-center py-10">
            <p className="text-lg font-editorial text-bronze/50">No order data available</p>
            <p className="text-sm text-bronze/30 mt-2">Order details could not be loaded</p>
          </div>
        )}
      </div>
    </SidebarWithHeader>
  );
}

export default SingleOrderPage;
